import React, { useState, useEffect, useCallback} from 'react';
import useAgentSummary from '../hooks/useAgentSummary';
import { NavLink, Link, Outlet, useNavigate, useLocation} from 'react-router-dom';
import { useDispatch, useSelector} from 'react-redux';
import { io } from 'socket.io-client';
import { logout} from '../redux/authSlice';
import { addNotification } from '../redux/notificationsSlice';
import SubscriptionGate from '../components/SubscriptionGate';
import { useTheme} from '../context/ThemeContext';
import {
 Menu,
 ChevronLeft, ChevronRight, ChevronDown, Search, Loader, Lock
} from 'lucide-react';
import * as Icons from 'lucide-react';
import api from '../services/api';
import { cn} from '../utils/cn';
import { dashboardGroups } from '../config/dashboardModules';
import { BrandLink } from '../components/BrandLogo';
import AssistantWidget from '../components/crm/AssistantWidget';
import NotificationBell from '../components/NotificationBell';
import '../styles/salesforce.css';

const CHANNEL_LABELS = { whatsapp: 'WhatsApp', instagram: 'Instagram', messenger: 'Messenger', support_widget: 'Support Widget' };

export default function DashboardLayout() {
 const dispatch = useDispatch();
 const navigate = useNavigate();
 const { summary: agentSummary } = useAgentSummary();
 const location = useLocation();
 const { user} = useSelector(state => state.auth);
 const { theme, toggleTheme} = useTheme();

 const [collapsed, setCollapsed] = useState(false);
 const [mobileOpen, setMobileOpen] = useState(false);
 const [menuQuery, setMenuQuery] = useState('');
 const [openGroups, setOpenGroups] = useState(() => {
   try {
     const saved = JSON.parse(localStorage.getItem('sidebarOpenGroups') || 'null');
     if (saved && typeof saved === 'object') return saved;
   } catch (e) {
     // fall through to default-all-open below
   }
   return dashboardGroups.reduce((acc, g) => ({ ...acc, [g.key]: true }), {});
 });

 // Tenant-configured External Modules (fintech/school-ERP/ecom/healthcare/legal
 // pages backed by any connected API) — dashboardModules.js itself stays a
 // static build-time file; this merges a dynamically-fetched section into the
 // render pipeline below, additive only, no change to the static groups.
 const [externalGroups, setExternalGroups] = useState([]);
 useEffect(() => {
   api.get('/support/external-modules')
     .then(res => {
       const modules = (res.data?.data || []).filter(m => m.isActive);
       if (!modules.length) return;
       const byGroup = {};
       modules.forEach(m => {
         const key = `external-${m.group}`;
         if (!byGroup[key]) byGroup[key] = { key, label: m.group, icon: Icons.Plug, color: '#0176D3', sections: [{ items: [] }] };
         byGroup[key].sections[0].items.push({
           to: `/dashboard/external/${m._id}`,
           icon: Icons[m.icon] || Icons.Plug,
           label: m.name,
         });
       });
       setExternalGroups(Object.values(byGroup));
     })
     .catch(() => {}); // no modules configured / feature unavailable — sidebar just shows the static groups
 }, []);

 const toggleGroup = useCallback((key) => {
   setOpenGroups((prev) => {
     const next = { ...prev, [key]: !prev[key] };
     localStorage.setItem('sidebarOpenGroups', JSON.stringify(next));
     return next;
   });
 }, []);

 // Global Search State
 const [globalQuery, setGlobalQuery] = useState('');
 const [results, setResults] = useState({ leads: [], deals: [], contacts: [], accounts: [] });
 const [isSearching, setIsSearching] = useState(false);
 const [showResults, setShowResults] = useState(false);

 useEffect(() => { setMobileOpen(false);}, [location.pathname]);
 useEffect(() => { setMenuQuery(''); }, [collapsed]);

 // App-wide notification feed for the header bell — this layout wraps every
 // /dashboard/* route and stays mounted across page navigation, so unlike the
 // page-scoped socket connections (InboxPage, InstagramDashboardLayout) this
 // is the one place a "you got a new message/comment" toast can fire no matter
 // which page the user is currently looking at.
 useEffect(() => {
   const token = localStorage.getItem('token');
   const socket = io('/inbox', { auth: { token }, transports: ['websocket'] });

   socket.on('inbox:new_message', ({ conversationId, message }) => {
     if (message?.direction !== 'inbound' || message?.isNote) return;
     const channelLabel = CHANNEL_LABELS[message.channel] || message.channel;
     const link = message.channel === 'instagram'
       ? `/dashboard/instagram/messages?conversationId=${conversationId}`
       : `/dashboard/inbox?conversationId=${conversationId}`;
     dispatch(addNotification({
       type: 'info',
       title: `New ${channelLabel} message`,
       message: message.content?.text || `[${message.type || 'attachment'}]`,
       link,
     }));
   });

   socket.on('instagram:new_comment', ({ mediaId, username, text }) => {
     dispatch(addNotification({
       type: 'info',
       title: 'New Instagram comment',
       message: username ? `@${username}: ${text || ''}` : (text || 'New comment on your Instagram content.'),
       link: `/dashboard/instagram/comments?mediaId=${mediaId}`,
     }));
   });

   return () => socket.disconnect();
 }, [dispatch]);

 // Separate, independent connection to the WhatsApp module's own namespace —
 // deliberately not merged with the /inbox listener above, matching that
 // module's real-time wiring being fully decoupled from Instagram/inbox.
 useEffect(() => {
   const token = localStorage.getItem('token');
   const socket = io('/whatsapp', { auth: { token }, transports: ['websocket'] });

   socket.on('whatsapp:new_message', ({ message }) => {
     if (message?.direction !== 'inbound') return;
     dispatch(addNotification({
       type: 'info',
       title: 'New WhatsApp message',
       message: message.messageText || `[${message.messageType || 'attachment'}]`,
       link: `/dashboard/meta/chat?recipient=${message.recipient}`,
     }));
   });

   return () => socket.disconnect();
 }, [dispatch]);

 const handleLogout = useCallback(() => { dispatch(logout()); navigate('/login');}, [dispatch, navigate]);

 // Global Search Logic
 useEffect(() => {
   const delayDebounce = setTimeout(async () => {
     if (globalQuery.length > 1) {
       setIsSearching(true);
       try {
         const res = await api.get('/crm/search', { params: { q: globalQuery } });
         setResults(res.data);
       } catch (err) {
         console.error('Search failed', err);
       } finally {
         setIsSearching(false);
       }
     } else {
       setResults({ leads: [], deals: [], contacts: [], accounts: [], invoices: [], quotations: [], vendors: [], bills: [] });
     }
   }, 400);

   return () => clearTimeout(delayDebounce);
 }, [globalQuery]);

 const initials = (user?.company || user?.name || user?.email || 'U')
 .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

 const q = menuQuery.trim().toLowerCase();
 // `effectiveFeatures` (populated by AuthController.getProfile, including
 // legacy-key alias expansion) is only absent for a moment before the
 // profile loads or for a plan-less account — in either case, showing
 // every item unlocked (features === null) beats flashing every module as
 // locked for a split second on every page load.
 const features = user?.effectiveFeatures ?? user?.subscriptionPlan?.features ?? null;
 const featureSet = features ? new Set(features) : null;
 const isLocked = (item) => !!(featureSet && item.feature && !featureSet.has(item.feature));

 const filteredGroups = [...dashboardGroups, ...externalGroups]
 .map((group) => ({
 key: group.key,
 label: group.label,
 icon: group.icon,
 color: group.color,
 sections: group.sections
 .map((section) => ({
 label: section.label,
 items: section.items
 .filter((item) => {
 if (!q) return true;
 return (
 group.label.toLowerCase().includes(q) ||
 (section.label || '').toLowerCase().includes(q) ||
 item.label.toLowerCase().includes(q) ||
 item.to.toLowerCase().includes(q)
 );
 })
 .map((item) => ({ ...item, locked: isLocked(item) })),
 }))
 .filter((section) => section.items.length > 0),
 }))
 .filter((group) => group.sections.length > 0);

 return (
 <div className="flex h-screen overflow-hidden bg-[var(--slds-color-bg)]">
 
 {/* Sidebar Overlay (Mobile Only) */}
 {mobileOpen && (
 <div className="fixed inset-0 bg-[#ffffff]/60 backdrop-blur-sm z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
 )}

  {/* ── SIDEBAR (SLDS Professional) ── */}
  <aside className={cn(
    "fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-[#D8DDE6] transition-all duration-300 ease-in-out lg:static lg:translate-x-0",
    collapsed ? "w-16" : "w-72",
    mobileOpen ? "translate-x-0" : "-translate-x-full"
  )}>
    <div className="h-14 flex items-center px-4 border-b border-[#D8DDE6] bg-white overflow-hidden shadow-sm">
      {!collapsed ? (
        <BrandLink to="/" size={32} subtitle="CRM" className="max-w-full" wordmarkClassName="pr-2" />
      ) : (
        <BrandLink to="/" size={30} showText={false} />
      )}
    </div>

 {!collapsed && (
 <div className="px-3 py-3 border-b border-[#EEF1F6] bg-white">
 <div className="flex items-center gap-2 px-3 py-2 bg-[#F4F6F9] border border-[#D8DDE6] rounded-[4px] focus-within:border-[#0176D3] focus-within:ring-1 focus-within:ring-[#0176D3]/20 transition-all">
 <Search size={14} className="text-[#706E6B]" />
 <input
 type="text"
 value={menuQuery}
 onChange={(e) => setMenuQuery(e.target.value)}
 placeholder="Filter menu…"
 className="w-full bg-transparent border-none outline-none text-[12px] text-[#080707] placeholder:text-[#706E6B] font-medium"
 />
 </div>
 </div>
 )}

 <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-2 space-y-1 no-scrollbar bg-white ">
 {filteredGroups.length ? filteredGroups.map((group) => {
 const isOpen = !!q || openGroups[group.key] !== false;
 return (
 <div key={group.key} className="space-y-1">
 <button
 type="button"
 onClick={() => toggleGroup(group.key)}
 className={cn(
 "w-full flex items-center gap-2 px-4 py-2 text-left rounded-md transition-colors group/navhead min-w-0",
 "hover:bg-[#F4F6F9]"
 )}
 title={collapsed ? group.label : undefined}
 >
 {!collapsed && (
 <ChevronDown
 size={13}
 strokeWidth={2.5}
 className={cn("text-[#706E6B] shrink-0 transition-transform duration-150", !isOpen && "-rotate-90")}
 />
 )}
 {group.icon && (
 <span
 className="w-5 h-5 rounded flex items-center justify-center shrink-0 transition-transform group-hover/navhead:scale-105"
 style={{ backgroundColor: `${group.color}1A`, color: group.color }}
 >
 <group.icon size={12} strokeWidth={2.5} />
 </span>
 )}
 <span className={cn(
 "text-[10px] font-bold text-[#706E6B] uppercase tracking-[0.15em] truncate min-w-0",
 collapsed && "sr-only"
 )}>{group.label}</span>
 </button>

 {(isOpen || collapsed) && (
 <div className="space-y-2">
 {group.sections.map((section, sIdx) => (
 <div key={sIdx} className="space-y-0.5">
 {!collapsed && section.label && (
 <div className="px-6 py-1">
 <span className="text-[9px] font-bold text-[#A8ADB4] uppercase tracking-[0.12em]">{section.label}</span>
 </div>
 )}
 {section.items.map((item) => {
 const isBlocked = item.comingSoon || item.locked;
 const isActive = location.pathname.startsWith(item.to) && !isBlocked;
 return (
 <NavLink
 key={item.label}
 to={isBlocked ? '#' : item.to}
 onClick={(e) => {
 if (item.comingSoon) {
 e.preventDefault();
 import('../utils/alert').then(m => m.default.toast('info', 'Coming Soon - Under Development'));
 } else if (item.locked) {
 e.preventDefault();
 navigate('/dashboard/subscription');
 import('../utils/alert').then(m => m.default.toast('info', `Upgrade your plan to unlock ${item.label}`));
 }
 }}
 className={cn(
 "flex items-center gap-3 px-4 py-2 leading-5 transition-colors text-[13px] font-medium rounded border-l-2",
 isActive
 ? "bg-[#EEF4FF] text-[#0176D3] font-semibold"
 : "border-transparent text-[#080707] hover:bg-[#F4F6F9] hover:text-[#0176D3]",
 isBlocked && "opacity-70 grayscale hover:grayscale-0"
 )}
 style={isActive ? { borderLeftColor: group.color || '#0176D3' } : undefined}
 title={collapsed ? (item.locked ? `${item.label} — upgrade to unlock` : item.label) : undefined}
 >
 <item.icon size={16} strokeWidth={2} className="shrink-0" aria-hidden="true" />
 {!collapsed && (
 <div className="flex-1 flex items-center justify-between min-w-0">
 <span className="truncate tracking-tight">{item.label}</span>
 {item.comingSoon && (
 <span className="text-[8px] bg-[#F3F2F2] px-1.5 py-0.5 rounded text-[#706E6B] font-bold uppercase tracking-widest shrink-0">Soon</span>
 )}
 {item.locked && !item.comingSoon && (
 <Lock size={11} className="text-[#A8ADB4] shrink-0" />
 )}
 {item.badge === 'agents' && agentSummary?.pendingCount > 0 && (
 <span className="text-[10px] bg-[#BA0517] text-white px-1.5 py-0.5 rounded-full font-bold shrink-0" aria-label={`${agentSummary.pendingCount} waiting for approval`}>{agentSummary.pendingCount}</span>
 )}
 </div>
 )}
 </NavLink>
 );
 })}
 </div>
 ))}
 </div>
 )}
 </div>
 );
 }) : !collapsed ? (
 <div className="px-4 py-10 text-center">
 <div className="text-[12px] font-bold text-[#080707]">No menu items found</div>
 <div className="text-[11px] text-[#706E6B] mt-1">Try a different keyword.</div>
 </div>
 ) : null}
 </nav>


 {/* Bottom Profile / Logout */}
 <div className="p-4 border-t border-[#D8DDE6] bg-white ">
 <div className={cn("flex items-center gap-3", collapsed ? "justify-center" : "")}>
 <div className="w-8 h-8 rounded-[4px] bg-[#0176D3] text-white font-bold text-xs flex items-center justify-center shrink-0 shadow-sm">
 {initials}
 </div>
 {!collapsed && (
 <div className="flex-1 min-w-0 pr-2">
 <p className="text-[11px] font-bold text-[#080707] truncate uppercase tracking-widest">{user?.company || 'Organization'}</p>
 <button onClick={handleLogout} className="text-[10px] font-bold text-[#706E6B] hover:text-[#BA0517] hover:bg-[#FDE7E9]/50 p-1 px-1.5 rounded transition-all">
 Secure Logout
 </button>
 </div>
 )}
 </div>
 </div>
 </aside>

  {/* ── MAIN CONTENT (SLDS Professional) ── */}
  <div className="flex-1 flex flex-col min-w-0 bg-[#F3F3F3] text-[#080707] ">
    
    {/* Header (Top Bar) */}
    <header className="h-14 flex items-center justify-between gap-2 px-3 sm:px-6 bg-white border-b border-[#D8DDE6] shadow-sm shrink-0 z-30">
      <div className="flex items-center gap-4">
        <button className="lg:hidden p-2 text-[#706E6B] hover:bg-[#F3F2F2] rounded-md transition-colors" onClick={() => setMobileOpen(true)}>
          <Menu size={20} />
        </button>
        <button className="hidden lg:flex items-center justify-center p-2 text-[#706E6B] hover:bg-[#F3F2F2] hover:text-[#0176D3] rounded-md transition-colors border border-transparent hover:border-[#D8DDE6]" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
        <div className="hidden lg:block">
          <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-[0.1em] leading-none mb-0.5">Organization</p>
          <p className="text-[13px] font-bold text-[#080707] truncate max-w-[200px] leading-none">{user?.company || 'Revenue Command'}</p>
        </div>
      </div>

      {/* Global Search */}
      <div className="flex-1 max-w-2xl px-8 hidden md:block relative group">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B] group-focus-within:text-[#0176D3] transition-colors" size={14} />
          <input 
            type="text" 
            placeholder="Global CRM search…" 
            value={globalQuery}
            onChange={(e) => setGlobalQuery(e.target.value)}
            onFocus={() => setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
            className="w-full h-9 bg-[#F3F2F2] border border-[#D8DDE6] rounded-md pl-10 pr-4 text-[13px] focus:bg-white focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none transition-all shadow-inner"
          />
          {isSearching && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader size={14} className="text-[#0176D3] animate-spin" />
            </div>
          )}
        </div>

        {/* Global Search Results Dropdown */}
        {showResults && (globalQuery.length > 1) && (
          <div className="absolute left-8 right-8 top-full mt-2 bg-white border border-[#D8DDE6] shadow-[0_2px_8px_rgba(0,0,0,0.16)] rounded-lg overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
            {Object.keys(results).some(k => results[k]?.length > 0) ? (
              <div className="max-h-[60vh] overflow-y-auto no-scrollbar py-2">
                {results.leads.length > 0 && (
                  <div className="mb-2">
                    <p className="px-4 py-1 bg-[#F4F6F9] text-[9px] font-bold text-[#706E6B] uppercase tracking-[0.2em]">Leads ({results.leads.length})</p>
                    {results.leads.map(lead => (
                      <div key={lead._id} className="px-4 py-2 hover:bg-[#F3F2F2] cursor-pointer flex items-center justify-between group" onClick={() => navigate(`/dashboard/crm/leads?id=${lead._id}`)}>
                        <span className="text-[13px] font-semibold text-[#080707] group-hover:text-[#0176D3]">{lead.name}</span>
                        <span className="text-[10px] text-[#706E6B] font-bold uppercase">{lead.status}</span>
                      </div>
                    ))}
                  </div>
                )}
                {results.deals.length > 0 && (
                  <div className="mb-2">
                    <p className="px-4 py-1 bg-[#F4F6F9] text-[9px] font-bold text-[#706E6B] uppercase tracking-[0.2em]">Deals ({results.deals.length})</p>
                    {results.deals.map(deal => (
                      <div key={deal._id} className="px-4 py-2 hover:bg-[#F3F2F2] cursor-pointer flex items-center justify-between group" onClick={() => navigate(`/dashboard/crm/deals/${deal._id}`)}>
                        <span className="text-[13px] font-semibold text-[#080707] group-hover:text-[#0176D3]">{deal.title}</span>
                        <span className="text-[11px] font-bold text-[#2E844A]">{deal.currency} {deal.value?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
                {results.contacts.length > 0 && (
                  <div className="mb-2">
                    <p className="px-4 py-1 bg-[#F4F6F9] text-[9px] font-bold text-[#706E6B] uppercase tracking-[0.2em]">Contacts ({results.contacts.length})</p>
                    {results.contacts.map(contact => (
                      <div key={contact._id} className="px-4 py-2 hover:bg-[#F3F2F2] cursor-pointer flex items-center justify-between group" onClick={() => navigate(`/dashboard/crm/contacts/${contact._id}`)}>
                        <span className="text-[13px] font-semibold text-[#080707] group-hover:text-[#0176D3]">{contact.name}</span>
                        <span className="text-[10px] text-[#706E6B]">{contact.email}</span>
                      </div>
                    ))}
                  </div>
                )}
                {[
                  ['invoices', 'Invoices', (x) => x.invoiceNumber, (x) => `${x.accountId?.name || x.contactId?.name || ''} · ${x.currency} ${x.totalAmount?.toLocaleString()} · ${x.status}`, () => '/dashboard/crm/invoices'],
                  ['quotations', 'Quotations', (x) => x.quotationNumber, (x) => `${x.accountId?.name || x.contactId?.name || ''} · ${x.currency} ${x.totalAmount?.toLocaleString()} · ${x.status}`, () => '/dashboard/crm/quotations'],
                  ['vendors', 'Vendors', (x) => x.name, (x) => x.gstin || x.city || '', () => '/dashboard/crm/vendors'],
                  ['bills', 'Vendor bills', (x) => `${x.billNumber}${x.vendorBillNumber ? ` · ${x.vendorBillNumber}` : ''}`, (x) => `${x.vendorId?.name || ''} · ${x.currency} ${x.totalAmount?.toLocaleString()} · ${x.status}`, () => '/dashboard/crm/purchase-bills'],
                ].map(([key, label, title, sub, to]) => (results[key]?.length > 0 && (
                  <div key={key} className="mb-2">
                    <p className="px-4 py-1 bg-[#F4F6F9] text-[9px] font-bold text-[#706E6B] uppercase tracking-[0.2em]">{label} ({results[key].length})</p>
                    {results[key].map(x => (
                      <div key={x._id} className="px-4 py-2 hover:bg-[#F3F2F2] cursor-pointer flex items-center justify-between gap-3 group" onClick={() => navigate(to(x))}>
                        <span className="text-[13px] font-semibold text-[#080707] group-hover:text-[#0176D3] truncate">{title(x)}</span>
                        <span className="text-[10px] text-[#706E6B] truncate">{sub(x)}</span>
                      </div>
                    ))}
                  </div>
                )))}
                {results.accounts.length > 0 && (
                  <div>
                    <p className="px-4 py-1 bg-[#F4F6F9] text-[9px] font-bold text-[#706E6B] uppercase tracking-[0.2em]">Accounts ({results.accounts.length})</p>
                    {results.accounts.map(account => (
                      <div key={account._id} className="px-4 py-2 hover:bg-[#F3F2F2] cursor-pointer flex items-center justify-between group" onClick={() => navigate(`/dashboard/crm/accounts/${account._id}`)}>
                        <span className="text-[13px] font-semibold text-[#080707] group-hover:text-[#0176D3]">{account.name}</span>
                        <span className="text-[10px] text-[#706E6B] uppercase">{account.industry}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : !isSearching ? (
              <div className="p-8 text-center">
                <Search className="mx-auto mb-2 opacity-10" size={32} />
                <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">Global Index: Zero results</p>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="flex items-center gap-4">
        <NotificationBell />

        <div className="w-8 h-8 rounded-full bg-[#0176D3] text-white flex items-center justify-center text-xs font-bold shadow-[0_2px_8px_rgba(0,0,0,0.16)] cursor-pointer hover:ring-2 hover:ring-[#0176D3]/20 transition-all">
          {initials}
        </div>
      </div>
    </header>

 {/* Scrollable Area */}
  <main className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 bg-[#F3F3F3] relative">
    <div className="p-3 sm:p-4 lg:p-6 h-full w-full max-w-full min-w-0">
 <SubscriptionGate>
 <Outlet />
 </SubscriptionGate>
 </div>
 </main>
 </div>

 <AssistantWidget />
 </div>
 );
}
